/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Fab,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@material-ui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RecurringPayableAttachmentModal from 'modules/common/RecurringPayableAttachmentModal.component';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { formatMoney } from 'utilities/currencyFormatter';
import { CreateExpenseCodeSearchInfo } from 'modules/common/expenseCodeHelper';
import SpinnerComponent from 'modules/common/spinner.component';
import RecurringPayableDetailsButtons from './recurringPayableDetailsButtons.component';
import RecurringPayableReportLineItem from './recurringPayableReportLineItemCard.component';

const useStyles = makeStyles(() => ({
  card: { width: '100%', flexGrow: 1, flexDirection: 'row', justifyContent: 'space-between' },
  cardContainer: { maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' },
  iconRow: { display: 'flex', flexDirection: 'row' },
  mb2: { marginBottom: '4px', marginRight: '16px' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' }
}));

const theme = createTheme({ palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING } });

const calcBalance = (items, rpAmount) =>
  (rpAmount ?? 0) - (Array.isArray(items) ? items.reduce((t, it) => t + (parseFloat(it && it.totalAmount) || 0), 0) : 0);

const RecurringPayableLineItemsCard = ({
  recurringPayableInfo,
  recurringPayableInfoForm,
  recurringPayableLineItems = [],
  expenseCodes = [],
  normalizeAuthSummaries = [],
  isReadOnly = false,
  onAddLine,
  onUpdateLine,
  onDeleteLine,
  updateRecurringPayable,
  setRecurringPayableInfoForm,
  isDirty,
  setIsLoading,
  isLoading,
  payToVendor,
  updateParentRPAttachments,
  updateParentRPLineAttachments,
  setExceptions,
  exceptions,
  clientId,
  enumerations
}) => {
  const classes = useStyles();

  const rpi = recurringPayableInfoForm?.recurringPayableInformation ?? {};
  const rpAmount = rpi?.amount ?? recurringPayableInfo?.recurringPayableInformation?.amount ?? 0;
  const rpId =
    recurringPayableInfoForm?.recurringPayableInformation?.recurringPayableId ??
    recurringPayableInfo?.recurringPayableInformation?.recurringPayableId;

  const clientName = rpi?.clientName ?? recurringPayableInfo?.recurringPayableInformation?.clientName ?? recurringPayableInfo?.clientName;

  const [items, setItems] = useState(recurringPayableLineItems);
  const [expandedId, setExpandedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    paymentAuthorizationId: payToVendor ? null : recurringPayableInfoForm?.payee?.authorizationId,
    expenseCodeId: null,
    expenseCode: '',
    totalAmount: '',
    description: '',
    onHoldDate: null,
    cat: ''
  });

  useEffect(() => setItems(recurringPayableLineItems), [recurringPayableLineItems]);

  const balance = useMemo(() => calcBalance(items, rpAmount) - (addOpen ? parseFloat(newItem.totalAmount) || 0 : 0), [
    items,
    rpAmount,
    addOpen,
    newItem.totalAmount
  ]);

  const codeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes), [expenseCodes]);

  const filteredAuthorizations = useMemo(
    () => (clientName ? normalizeAuthSummaries.filter(a => a.clientName === clientName) : normalizeAuthSummaries),
    [normalizeAuthSummaries, clientName]
  );

  const resetNew = () => {
    setNewItem({
      paymentAuthorizationId: payToVendor ? null : recurringPayableInfoForm?.payee?.authorizationId,
      expenseCodeId: null,
      expenseCode: '',
      totalAmount: '',
      description: '',
      onHoldDate: null,
      cat: ''
    });
  };

  const handleAddClick = () => {
    if (!payToVendor) {
      setNewItem(prev => ({ ...prev, paymentAuthorizationId: recurringPayableInfoForm?.payee?.authorizationId }));
    }
    setAddOpen(true);
  };

  const handleCancelAdd = () => {
    setAddOpen(false);
    resetNew();
  };

  const handleSaveNew = async () => {
    setIsLoading(true);
    const payload = {
      recurringPayableId: rpId,
      paymentAuthorizationId: newItem.paymentAuthorizationId,
      expenseCodeId: newItem.expenseCodeId,
      totalAmount: Number(newItem.totalAmount || 0),
      description: newItem.description || '',
      onHoldDate: newItem.onHoldDate
    };
    const ok = await onAddLine(payload);
    if (ok) {
      setAddOpen(false);
      resetNew();
    }
    setIsLoading(false);
  };

  const handleDelete = async id => {
    const ok = await onDeleteLine(id);
    if (ok) {
      setItems(prev => prev.filter(i => i.lineItemId !== id));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ mt: '10px' }}>
        {isLoading && <SpinnerComponent />}
        {!isLoading && (
          <Box>
            <CardContent className={classes.cardContainer}>
              <div className="row justify-space-between align-center" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div className={classes.iconRow}>
                  <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
                  <Typography variant="h5" sx={{ pl: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                    Line Items
                  </Typography>
                </div>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography>
                    Balance:&nbsp;
                    <span style={{ fontWeight: 'bold', color: balance === 0 ? undefined : 'red' }}>{formatMoney(balance)}</span>
                  </Typography>
                </div>
                <div style={{ visibility: 'hidden', width: 250 }} />
              </div>
            </CardContent>

            <CardContent sx={{ p: '10px', ml: '10px', pt: 0 }}>
              <Box sx={{ mt: '16px' }}>
                <Paper>
                  <Box sx={{ p: '16px', pt: '8px' }}>
                    <Box sx={{ pb: '10px', borderBottom: '2px solid' }}>
                      <Grid container spacing={1} columns={20}>
                        <Grid item md={3}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Authorization
                          </Typography>
                        </Grid>
                        <Grid item md={1}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            CAT
                          </Typography>
                        </Grid>
                        <Grid item md={3}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Code
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Amount
                          </Typography>
                        </Grid>
                        <Grid item md={5}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Line Desc
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            On Hold
                          </Typography>
                        </Grid>
                        <Grid item md={2}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Attachment
                          </Typography>
                        </Grid>
                        <Grid item md={1}>
                          <Typography variant="subtitle2" sx={{ color: '#777667' }}>
                            Edit
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {(!items || items.length === 0) && (
                      <Box sx={{ pt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="subtitle2">No line items. Please add at least one line item.</Typography>
                      </Box>
                    )}

                    {items?.map(row => (
                      <Box key={row.lineItemId} sx={{ pb: '10px', pt: '10px', borderBottom: '2px solid' }}>
                        {expandedId !== row.lineItemId ? (
                          <Grid container spacing={1} columns={20}>
                            <Grid item md={3}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {normalizeAuthSummaries.find(a => a.fileId === row.paymentAuthorizationId)?.transfereeName}
                              </Typography>
                            </Grid>
                            <Grid item md={1}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {expenseCodes.find(e => e.expenseCodeId === row.expenseCodeId)?.category}
                              </Typography>
                            </Grid>
                            <Grid item md={3}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {row.expenseCodeId}
                              </Typography>
                            </Grid>
                            <Grid item md={2}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {row.totalAmount}
                              </Typography>
                            </Grid>
                            <Grid item md={5}>
                              <Typography variant="body2" sx={{ pt: '10px', fontWeight: 500 }}>
                                {row.description}
                              </Typography>
                            </Grid>
                            <Grid item md={2}>
                              <Checkbox checked={!!row.onHoldDate} disabled />
                            </Grid>
                            <Grid item md={2}>
                              <RecurringPayableAttachmentModal 
                                displayText={`View Attachments (${recurringPayableInfoForm && recurringPayableInfoForm.recurringPayableLineAttachments ? recurringPayableInfoForm.recurringPayableLineAttachments.filter((rla) => rla.recurringPayableLineItemId === row.lineItemId).length : 0})`} 
                                isReadOnly={isReadOnly} 
                                recurringPayableLineInfo={recurringPayableInfoForm.recurringPayableInformation} 
                                recurringPayableAttachments={recurringPayableInfoForm.recurringPayableAttachments} 
                                recurringPayableLineAttachments={recurringPayableInfoForm && recurringPayableInfoForm.recurringPayableLineAttachments ? recurringPayableInfoForm.recurringPayableLineAttachments.filter((rla) => rla.recurringPayableLineItemId === row.lineItemId) : []} 
                                updateParentRPAttachments={updateParentRPAttachments} 
                                updateParentRPLineAttachments={updateParentRPLineAttachments} 
                                lineItemId={row.lineItemId}
                              />
                            </Grid>
                            <Grid item md={1}>
                              <Fab
                                className={classes.iconOverlayImg}
                                color="primary"
                                size="small"
                                onClick={() => setExpandedId(row.lineItemId)}
                                disabled={isReadOnly || addOpen}
                              >
                                <FontAwesomeIcon size="1x" icon={['fas', 'pen']} color="#fff" />
                              </Fab>
                            </Grid>
                            <Box sx={{ pl: 1, pt: 1 }}>
                              <Button
                                color="primary"
                                variant="contained"
                                sx={{ backgroundColor: '#36939b', '&:hover': { backgroundColor: '#15585e' }, color: '#fff', minWidth: 120 }}
                                onClick={() => handleDelete(row.lineItemId)}
                                disabled={isReadOnly}
                              >
                                Delete Line
                              </Button>
                            </Box>
                          </Grid>
                        ) : (
                          <RecurringPayableReportLineItem
                            isReadOnly={isReadOnly}
                            lineItem={row}
                            closeLineItem={() => setExpandedId(null)}
                            expenseCodes={expenseCodes}
                            authorizationSummaries={filteredAuthorizations}
                            rpInfoForm={recurringPayableInfoForm ?? recurringPayableInfo}
                            onUpdateLine={onUpdateLine}
                            setFormLineItems={setItems}
                            payToVendor={payToVendor}
                            updateParentRPAttachments={updateParentRPAttachments}
                            updateParentRPLineAttachments={updateParentRPLineAttachments}
                            exceptions={exceptions ? exceptions.exceptionSumamries : []}
                            setExceptions={setExceptions}
                            clientId={clientId}
                            enumerations={enumerations}
                          />
                        )}
                      </Box>
                    ))}

                    {addOpen && (
                      <Box sx={{ pb: '10px', pt: '10px', borderBottom: '2px solid' }}>
                        <Grid container spacing={1} columns={20}>
                          <Grid item md={3}>
                            {payToVendor ? (
                              <AdvancedSearch
                                labelText={'Authorization'}
                                displayValueAccessorKey={'transfereeName'}
                                saveIdValueAccessorKey={'fileId'}
                                searchingCriteria={[
                                  { name: 'fileId', title: 'Auth Id' },
                                  { name: 'transfereeName', title: 'Full Name' },
                                  { name: 'departure', title: 'Departure' },
                                  { name: 'destination', title: 'Destination' },
                                  { name: 'clientName', title: 'Client' }
                                ]}
                                searchingCriteriaExtensions={[
                                  { columnName: 'fileId', width: 150 },
                                  { columnName: 'transfereeName', width: 250 },
                                  { name: 'departure', width: 175 },
                                  { name: 'destination', width: 175 },
                                  { columnName: 'clientName', width: 175 }
                                ]}
                                searchingRows={filteredAuthorizations}
                                id={newItem.paymentAuthorizationId}
                                value={filteredAuthorizations.find(a => a.fileId === newItem.paymentAuthorizationId)?.transfereeName}
                                setIdValue={id => setNewItem(prev => ({ ...prev, paymentAuthorizationId: id }))}
                                isReadOnly={isReadOnly}
                              />
                            ) : (
                              <TextField
                                disabled
                                value={normalizeAuthSummaries.find(a => a.fileId === newItem.paymentAuthorizationId)?.transfereeName}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          </Grid>

                          <Grid item md={1}>
                            <Box sx={{ pt: '10px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {newItem.cat || '—'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item md={3}>
                            <AdvancedSearch
                              labelText={''}
                              displayValueAccessorKey={codeSearchInfo.nameKey}
                              saveIdValueAccessorKey={codeSearchInfo.idKey}
                              searchingCriteria={codeSearchInfo.searchingColumns}
                              searchingCriteriaExtensions={codeSearchInfo.searchingColumnExtensions}
                              searchingRows={codeSearchInfo.searchingRows}
                              id={newItem.expenseCodeId}
                              value={newItem.expenseCode}
                              setIdValue={(id, value) => {
                                const info = expenseCodes.find(e => e.expenseCodeId === id);
                                setNewItem(prev => ({
                                  ...prev,
                                  expenseCodeId: id,
                                  expenseCode: value,
                                  cat: info?.category || ''
                                }));
                              }}
                              maxWidthsx="360px"
                              isReadOnly={isReadOnly}
                            />
                          </Grid>

                          <Grid item md={2}>
                            <TextField
                              value={newItem.totalAmount}
                              fullWidth
                              type="number"
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              disabled={isReadOnly}
                              onChange={e => setNewItem(prev => ({ ...prev, totalAmount: e.target.value }))}
                            />
                          </Grid>

                          <Grid item md={5}>
                            <TextField
                              value={newItem.description}
                              fullWidth
                              disabled={isReadOnly}
                              InputLabelProps={{ shrink: true }}
                              onChange={e => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                            />
                          </Grid>

                          <Grid item md={2} sx={{ ml: '10px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!newItem.onHoldDate}
                                  disabled={isReadOnly}
                                  onChange={() => setNewItem(prev => ({ ...prev, onHoldDate: prev.onHoldDate ? null : new Date() }))}
                                />
                              }
                              label="On Hold"
                            />
                          </Grid>

                          {/* <Grid item md={2}>
                            <AttachmentModal displayText={'View Attachment'} />
                          </Grid> */}

                          <Grid item md={7} />
                          <Grid item md={10}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                              <Button variant="contained" onClick={handleCancelAdd}>
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveNew}
                                disabled={
                                  isReadOnly ||
                                  !rpId ||
                                  (!payToVendor && !newItem.paymentAuthorizationId) ||
                                  !newItem.expenseCodeId ||
                                  Number(newItem.totalAmount || 0) <= 0
                                }
                              >
                                Save Line
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '10px' }}>
                      <Button
                        color="primary"
                        variant="contained"
                        sx={{ backgroundColor: '#36939b', '&:hover': { backgroundColor: '#15585e' }, color: '#fff', minWidth: 120 }}
                        onClick={handleAddClick}
                        disabled={isReadOnly || addOpen || !!expandedId}
                      >
                        Add Line
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ mt: '16px', display: 'flex', justifyContent: 'space-between', px: '24px', pb: '10px' }}>
                    <RecurringPayableDetailsButtons
                      isFormValidForApproval={
                        balance === 0 && (Array.isArray(recurringPayableLineItems) ? recurringPayableLineItems.length > 0 : false)
                      }
                      rpStatusId={
                        recurringPayableInfoForm?.recurringPayableInformation?.recurringPayableStatusId ??
                        recurringPayableInfo?.recurringPayableInformation?.recurringPayableStatusId ??
                        ''
                      }
                      rpInfoForm={recurringPayableInfoForm}
                      rpInfo={recurringPayableInfo}
                      isDirty={isDirty && !addOpen && !expandedId}
                      updateRecurringPayable={updateRecurringPayable}
                      setRecurringPayableInfoForm={setRecurringPayableInfoForm}
                      addOpen={addOpen}
                      expandedId={expandedId}
                    />
                  </Box>
                </Paper>
              </Box>
            </CardContent>
          </Box>
        )}
      </Card>
    </ThemeProvider>
  );
};

export default RecurringPayableLineItemsCard;
